import { LngLat } from '@yandex/ymaps3-types';
import { YMapLocationRequest } from '@yandex/ymaps3-types/imperative/YMap';
import {
  Feature as ClustererFeature,
  IClusterMethod
} from '@yandex/ymaps3-types/packages/clusterer';
import { useCallback } from 'react';

import { ReactifyApi, Ymaps3Clusterer } from '@/fo/ui/mod/yandex/maps';

import { MARGIN, MOBILE_MARGIN } from '../../consts';
import { PointFeature } from '../../types';
import { PickupCluster } from '../PickupCluster/PickupCluster';
import { PickupMarker } from '../PickupMarker/PickupMarker';

import { styles } from './MapContent.styles';

export interface MapContentProps {
  reactifyApi: ReactifyApi;
  clusterer: Ymaps3Clusterer;
  gridSizedMethod: IClusterMethod;
  pointsList: PointFeature[];
  selectedPoint: PointFeature | null;
  location: YMapLocationRequest;
  isDesktop: boolean;
  onMarkerClick: (feature: PointFeature) => void;
  onClusterClick: (features: ClustererFeature[]) => void;
}

/**
 * Компонент для отображения карты с маркерами и кластерами
 */
export function MapContent({
  reactifyApi,
  clusterer,
  gridSizedMethod,
  pointsList,
  selectedPoint,
  location,
  isDesktop,
  onMarkerClick,
  onClusterClick
}: MapContentProps) {
  const slots = styles();

  const { YMap, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer, YMapMarker } =
    reactifyApi;

  const { YMapClusterer } = clusterer;

  /**
   * Рендер маркера
   */
  const marker = useCallback(
    (feature: ClustererFeature) => (
      <YMapMarker
        coordinates={[
          feature.geometry.coordinates[0],
          feature.geometry.coordinates[1]
        ]}
      >
        <PickupMarker
          feature={feature as PointFeature}
          onClick={onMarkerClick}
          isSelected={selectedPoint?.id === feature.id}
        />
      </YMapMarker>
    ),
    [YMapMarker, onMarkerClick, selectedPoint?.id]
  );

  /**
   * Рендер кластера
   */
  const cluster = useCallback(
    (coordinates: LngLat, features: ClustererFeature[]) => (
      <YMapMarker coordinates={coordinates}>
        <PickupCluster
          features={features}
          onClick={onClusterClick}
        />
      </YMapMarker>
    ),
    [YMapMarker, onClusterClick]
  );

  return (
    <div className={slots.base()}>
      <YMap
        location={location}
        margin={isDesktop ? MARGIN : MOBILE_MARGIN}
      >
        <YMapDefaultSchemeLayer />
        <YMapDefaultFeaturesLayer />

        <YMapClusterer
          features={pointsList}
          method={gridSizedMethod}
          marker={marker}
          cluster={cluster}
        />
      </YMap>
    </div>
  );
}
